import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customers as customersApi, wallet as walletApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import {
  Loading, Empty, Modal, Can, Pagination, StatusBadge, money, dateOnly,
} from '../components/Common';

export default function Customers() {
  const toast = useToast();

  const [data, setData] = useState({ customers: [], meta: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await customersApi.list({ page, limit: 20, search: search || undefined });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load customers', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Customers · UrbanClean'; }, []);

  const open = async (customer) => {
    try {
      const res = await customersApi.detail(customer._id);
      setDetail(res.data);
    } catch (err) {
      toast.error('Could not load the customer', err.message);
    }
  };

  const toggleBlock = async (customer) => {
    try {
      const res = await customersApi.toggleBlock(customer._id);
      toast.success(res.message);
      load();
    } catch (err) {
      toast.error('Could not update', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Customers</h1>
          <div className="sub">Customer database, order history and loyalty</div>
        </div>
        <Can permission="customer.create">
          <button className="btn btn-blue" onClick={() => setCreating(true)}>+ Add customer</button>
        </Can>
      </div>

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search name, mobile or email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ minWidth: 260 }}
        />
      </div>

      <div className="card">
        {loading ? <Loading /> : data.customers.length === 0 ? (
          <Empty title="No customers yet" body="They appear here after their first sign-in or walk-in order." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Customer</th><th>Referral</th><th className="center">Orders</th>
                    <th className="right">Spent</th><th className="right">Wallet</th>
                    <th>Joined</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c._id}>
                      <td>
                        <div className="strong">{c.name || 'Unnamed'}</div>
                        <div className="muted" style={{ fontSize: 12 }}>{c.mobile}</div>
                        {c.isBlocked ? <span className="badge badge-red" style={{ marginTop: 4 }}>Blocked</span> : null}
                      </td>
                      <td className="mono muted">{c.referralCode}</td>
                      <td className="center">{c.totalOrders}</td>
                      <td className="right strong">{money(c.totalSpent)}</td>
                      <td className="right">{money(c.walletBalance)}</td>
                      <td className="muted nowrap">{dateOnly(c.createdAt)}</td>
                      <td className="right nowrap">
                        <button className="btn btn-sm btn-ghost" onClick={() => open(c)}>View</button>
                        <Can permission="customer.update">
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6 }} onClick={() => toggleBlock(c)}>
                            {c.isBlocked ? 'Unblock' : 'Block'}
                          </button>
                        </Can>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={setPage} />
          </>
        )}
      </div>

      <Modal open={Boolean(detail)} wide title={detail?.user?.name || detail?.user?.mobile || ''} onClose={() => setDetail(null)}>
        {detail ? (
          <>
            <div className="grid grid-4 mb">
              <div className="stat"><div className="stat-bar navy" /><div className="stat-label">Orders</div><div className="stat-value">{detail.stats.totalOrders}</div></div>
              <div className="stat"><div className="stat-bar blue" /><div className="stat-label">Spent</div><div className="stat-value" style={{ fontSize: 20 }}>{money(detail.stats.totalSpent)}</div></div>
              <div className="stat"><div className="stat-bar green" /><div className="stat-label">Urban Wallet</div><div className="stat-value" style={{ fontSize: 20 }}>{detail.user.walletBalance} pts</div></div>
              <div className="stat"><div className="stat-bar amber" /><div className="stat-label">Loyalty</div><div className="stat-value">{detail.user.loyaltyPoints}</div></div>
            </div>

            <WalletPanel userId={detail.user._id} onChanged={() => open(detail.user)} />

            <h3 style={{ fontSize: 14, marginBottom: 10 }}>Order history</h3>
            {detail.orders.length === 0 ? <Empty title="No orders yet" /> : (
              <div className="table-wrap">
                <table className="data">
                  <thead><tr><th>Order</th><th>Status</th><th className="right">Total</th><th>Date</th></tr></thead>
                  <tbody>
                    {detail.orders.map((o) => (
                      <tr key={o._id}>
                        <td><Link to={`/orders/${o._id}`} className="mono strong">{o.orderNumber}</Link></td>
                        <td><StatusBadge status={o.status} /></td>
                        <td className="right strong">{money(o.total)}</td>
                        <td className="muted nowrap">{dateOnly(o.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
      </Modal>

      <CreateCustomer open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
    </>
  );
}

/**
 * A customer's Urban Wallet, for support.
 *
 * The balance on its own is not much use when someone rings up asking where
 * their points went — the ledger is, so it is shown in full. Adjusting goes
 * through the same ledger with the staff member recorded against it, which is
 * why a reason is required rather than optional.
 */
function WalletPanel({ userId, onChanged }) {
  const toast = useToast();
  const [ledger, setLedger] = useState(null);
  const [points, setPoints] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await walletApi.ledger(userId, { limit: 10 });
      setLedger(res.data);
    } catch (err) {
      // Staff without payment.view simply do not see the panel.
      setLedger(null);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const adjust = async (sign) => {
    const value = Math.round(Number(points)) * sign;
    if (!value) return toast.error('Enter the points to add or remove');
    if (!note.trim()) return toast.error('Say why this adjustment was made');

    setBusy(true);
    try {
      await walletApi.adjust({ userId, points: value, note: note.trim() });
      toast.success(value > 0 ? `${value} points added` : `${-value} points removed`);
      setPoints('');
      setNote('');
      load();
      onChanged?.();
    } catch (err) {
      toast.error('Could not update the wallet', err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!ledger) return null;

  return (
    <div className="card mb">
      <div className="card-head">
        <h3>Urban Wallet</h3>
        <span className="badge badge-green">{ledger.balance} points</span>
      </div>

      <div className="card-body">
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0, width: 110 }}>
            <label>Points</label>
            <input
              className="input"
              value={points}
              onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              placeholder="100"
            />
          </div>
          <div className="field" style={{ marginBottom: 0, flex: 1 }}>
            <label>Reason</label>
            <input
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Goodwill for a delayed order"
            />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={() => adjust(1)}>Add</button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => adjust(-1)}>Remove</button>
        </div>

        {ledger.transactions.length === 0 ? (
          <div className="muted mt" style={{ fontSize: 12.5 }}>No wallet activity yet.</div>
        ) : (
          <div className="table-wrap mt">
            <table className="data">
              <thead>
                <tr>
                  <th>Movement</th><th className="right">Points</th>
                  <th className="right">Balance</th><th>When</th>
                </tr>
              </thead>
              <tbody>
                {ledger.transactions.map((t) => (
                  <tr key={t._id}>
                    <td>
                      <span style={{ textTransform: 'capitalize' }}>{t.type}</span>
                      {t.orderNumber ? <span className="muted"> · {t.orderNumber}</span> : null}
                      {t.note ? <div className="muted" style={{ fontSize: 12 }}>{t.note}</div> : null}
                    </td>
                    <td className="right strong" style={{ color: t.points > 0 ? 'var(--green)' : 'var(--blue)' }}>
                      {t.points > 0 ? '+' : '−'}{Math.abs(t.points)}
                    </td>
                    <td className="right muted">{t.balanceAfter}</td>
                    <td className="muted nowrap">{dateOnly(t.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateCustomer({ open, onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', mobile: '', email: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm({ name: '', mobile: '', email: '' }); }, [open]);

  const submit = async () => {
    if (!form.mobile) return toast.error('Mobile number is required');
    setBusy(true);
    try {
      const res = await customersApi.create(form);
      toast.success(res.message);
      onCreated();
    } catch (err) {
      toast.error('Could not add the customer', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title="Add a customer" onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Add customer'}</button>
      </>}>
      <div className="field">
        <label>Mobile number *</label>
        <input className="input" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
        <div className="hint">If this number already exists, the existing record is reused.</div>
      </div>
      <div className="field">
        <label>Name</label>
        <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div className="field">
        <label>Email</label>
        <input className="input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
      </div>
    </Modal>
  );
}
